import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Users, Crown, BarChart3, Database, Shield, Settings, Star, TrendingUp, DollarSign, Zap, RefreshCw, Infinity } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/Card';
import { Button } from './ui/Button';
import { FullScreenLoading } from './ui/LoadingSpinner';
import { adminApi, AdminUserResponse, AdminCreditUpdate, AdminCreditReset, AdminUnlimitedToggle } from '../services/api';
import { UsageDashboard } from './admin/UsageDashboard';
import { CreditUsageDashboard } from './admin/CreditUsageDashboard';

interface SystemStats {
  total_users: number;
  total_courses: number;
  total_sessions: number;
  total_practice_sessions: number;
  vip_users: number;
}

const AdminDashboard: React.FC = () => {
  const { t } = useTranslation();
  const [users, setUsers] = useState<AdminUserResponse[]>([]);
  const [stats, setStats] = useState<SystemStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState<number | null>(null);
  const [creditOperation, setCreditOperation] = useState<{ userId: number; type: 'grant' | 'reset' | 'unlimited' } | null>(null);
  const [editingCredits, setEditingCredits] = useState<{ userId: number; value: string } | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'usage' | 'credits'>('overview');

  useEffect(() => {
    loadAdminData();
  }, []);

  const loadAdminData = async () => {
    try {
      setLoading(true);
      const [usersData, statsData] = await Promise.all([
        adminApi.getAllUsers(),
        adminApi.getSystemStats()
      ]);
      setUsers(usersData);
      setStats(statsData);
    } catch (error) {
      console.error('Failed to load admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleVip = async (userId: number, currentVipStatus: boolean) => {
    try {
      setUpdating(userId);
      const updatedUser = await adminApi.updateUser(userId, { is_vip: !currentVipStatus });
      setUsers(users.map(user => 
        user.id === userId ? updatedUser : user
      ));
      // Update stats
      if (stats) {
        const newVipCount = !currentVipStatus ? stats.vip_users + 1 : stats.vip_users - 1;
        setStats({ ...stats, vip_users: newVipCount });
      }
    } catch (error) {
      console.error('Failed to update user:', error);
    } finally {
      setUpdating(null);
    }
  };

  const handleSetCredits = async (userId: number, newCredits: number) => {
    try {
      setCreditOperation({ userId, type: 'grant' });
      const user = users.find(u => u.id === userId);
      if (!user) return;
      
      // Calculate the difference to add/subtract
      const currentCredits = user.credits_remaining >= 999999 ? 0 : user.credits_remaining;
      const creditsToAdd = newCredits - currentCredits;
      
      const updatedUser = await adminApi.grantCredits(userId, { 
        credits_to_add: creditsToAdd,
        operation_type: 'admin_set'
      });
      setUsers(users.map(u => 
        u.id === userId ? updatedUser : u
      ));
      setEditingCredits(null);
    } catch (error) {
      console.error('Failed to set credits:', error);
    } finally {
      setCreditOperation(null);
    }
  };

  const handleCreditEdit = (userId: number, currentCredits: number) => {
    const credits = currentCredits >= 999999 ? '999999' : currentCredits.toString();
    setEditingCredits({ userId, value: credits });
  };

  const handleCreditSave = (userId: number) => {
    if (!editingCredits) return;
    const newCredits = parseInt(editingCredits.value);
    if (isNaN(newCredits) || newCredits < 0) {
      setEditingCredits(null);
      return;
    }
    handleSetCredits(userId, newCredits);
  };

  const handleCreditCancel = () => {
    setEditingCredits(null);
  };

  const handleResetCredits = async (userId: number) => {
    try {
      setCreditOperation({ userId, type: 'reset' });
      const updatedUser = await adminApi.resetCredits(userId);
      setUsers(users.map(user => 
        user.id === userId ? updatedUser : user
      ));
    } catch (error) {
      console.error('Failed to reset credits:', error);
    } finally {
      setCreditOperation(null);
    }
  };

  const handleToggleUnlimited = async (userId: number, currentUnlimitedStatus: boolean) => {
    try {
      setCreditOperation({ userId, type: 'unlimited' });
      const updatedUser = await adminApi.toggleUnlimited(userId, { 
        is_unlimited: !currentUnlimitedStatus 
      });
      setUsers(users.map(user => 
        user.id === userId ? updatedUser : user
      ));
    } catch (error) {
      console.error('Failed to toggle unlimited access:', error);
    } finally {
      setCreditOperation(null);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <FullScreenLoading 
        variant="astronaut" 
        message="Loading admin dashboard..." 
        emoji="🛸"
      />
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center space-y-4">
        <h1 className="text-4xl md:text-6xl font-bold text-bg-secondary flex items-center justify-center gap-3">
          <Shield className="w-8 h-8 md:w-12 md:h-12 text-muted-purple" />
          Admin Dashboard
        </h1>
        <p className="text-xl text-bg-secondary">
          System overview and user management
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="flex justify-center space-x-1">
        <button
          onClick={() => setActiveTab('overview')}
          className={`px-6 py-3 rounded-lg font-medium transition-all duration-300 ${
            activeTab === 'overview'
              ? 'bg-vibrant-orange text-white shadow-lg'
              : 'bg-white text-surface-primary hover:bg-surface-primary/10 hover:text-vibrant-orange border border-surface-muted/30'
          }`}
        >
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            Overview
          </div>
        </button>
        <button
          onClick={() => setActiveTab('usage')}
          className={`px-6 py-3 rounded-lg font-medium transition-all duration-300 ${
            activeTab === 'usage'
              ? 'bg-vibrant-orange text-white shadow-lg'
              : 'bg-white text-surface-primary hover:bg-surface-primary/10 hover:text-vibrant-orange border border-surface-muted/30'
          }`}
        >
          <div className="flex items-center gap-2">
            <DollarSign className="w-4 h-4" />
            Usage Analytics
          </div>
        </button>
        <button
          onClick={() => setActiveTab('credits')}
          className={`px-6 py-3 rounded-lg font-medium transition-all duration-300 ${
            activeTab === 'credits'
              ? 'bg-vibrant-orange text-white shadow-lg'
              : 'bg-white text-surface-primary hover:bg-surface-primary/10 hover:text-vibrant-orange border border-surface-muted/30'
          }`}
        >
          <div className="flex items-center gap-2">
            <Zap className="w-4 h-4" />
            Credit Analytics
          </div>
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <>
          {/* System Stats */}
          {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
          <Card className="text-center">
            <CardContent className="pt-6">
              <div className="flex items-center justify-center mb-3">
                <div className="w-16 h-16 bg-gradient-to-br from-vibrant-orange/10 to-vibrant-orange/20 rounded-full flex items-center justify-center">
                  <Users className="w-8 h-8 text-vibrant-orange" />
                </div>
              </div>
              <div className="text-3xl font-bold text-surface-primary">{stats.total_users}</div>
              <div className="text-sm text-surface-muted font-medium">Total Users</div>
            </CardContent>
          </Card>

          <Card className="text-center">
            <CardContent className="pt-6">
              <div className="flex items-center justify-center mb-3">
                <div className="w-16 h-16 bg-gradient-to-br from-galaxy-pink/10 to-galaxy-pink/20 rounded-full flex items-center justify-center">
                  <Crown className="w-8 h-8 text-galaxy-pink" />
                </div>
              </div>
              <div className="text-3xl font-bold text-surface-primary">{stats.vip_users}</div>
              <div className="text-sm text-surface-muted font-medium">VIP Users</div>
            </CardContent>
          </Card>

          <Card className="text-center">
            <CardContent className="pt-6">
              <div className="flex items-center justify-center mb-3">
                <div className="w-16 h-16 bg-gradient-to-br from-muted-purple/10 to-muted-purple/20 rounded-full flex items-center justify-center">
                  <Database className="w-8 h-8 text-muted-purple" />
                </div>
              </div>
              <div className="text-3xl font-bold text-surface-primary">{stats.total_courses}</div>
              <div className="text-sm text-surface-muted font-medium">Total Courses</div>
            </CardContent>
          </Card>

          <Card className="text-center">
            <CardContent className="pt-6">
              <div className="flex items-center justify-center mb-3">
                <div className="w-16 h-16 bg-gradient-to-br from-vibrant-orange/10 to-vibrant-orange/20 rounded-full flex items-center justify-center">
                  <BarChart3 className="w-8 h-8 text-vibrant-orange" />
                </div>
              </div>
              <div className="text-3xl font-bold text-surface-primary">{stats.total_sessions}</div>
              <div className="text-sm text-surface-muted font-medium">Study Sessions</div>
            </CardContent>
          </Card>

          <Card className="text-center">
            <CardContent className="pt-6">
              <div className="flex items-center justify-center mb-3">
                <div className="w-16 h-16 bg-gradient-to-br from-galaxy-pink/10 to-galaxy-pink/20 rounded-full flex items-center justify-center">
                  <TrendingUp className="w-8 h-8 text-galaxy-pink" />
                </div>
              </div>
              <div className="text-3xl font-bold text-surface-primary">{stats.total_practice_sessions}</div>
              <div className="text-sm text-surface-muted font-medium">Practice Sessions</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Users Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-surface-primary flex items-center gap-2">
            <Users className="w-5 h-5 text-muted-purple" />
            User Management
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-surface-muted/30">
                  <th className="text-left p-4 font-semibold text-surface-muted">ID</th>
                  <th className="text-left p-4 font-semibold text-surface-muted">Email</th>
                  <th className="text-left p-4 font-semibold text-surface-muted">Gender</th>
                  <th className="text-left p-4 font-semibold text-surface-muted">VIP Status</th>
                  <th className="text-left p-4 font-semibold text-surface-muted">Subscription</th>
                  <th className="text-left p-4 font-semibold text-surface-muted">Credits</th>
                  <th className="text-left p-4 font-semibold text-surface-muted">Stats</th>
                  <th className="text-left p-4 font-semibold text-surface-muted">Created</th>
                  <th className="text-left p-4 font-semibold text-surface-muted">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} className="border-b border-surface-muted/20 hover:bg-surface-primary/30 transition-colors duration-200">
                    <td className="p-4 text-surface-muted font-mono">{user.id}</td>
                    <td className="p-4 text-surface-primary font-medium">{user.email}</td>
                    <td className="p-4 text-surface-muted">
                      {user.gender === 'male' ? '👨' : '👩'} {user.gender}
                    </td>
                    <td className="p-4">
                      {user.is_vip ? (
                        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-gradient-to-r from-yellow-400/20 to-vibrant-orange/20 text-vibrant-orange border border-vibrant-orange/30 text-sm font-medium">
                          <Crown className="w-4 h-4" />
                          VIP
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-surface-muted/20 text-surface-muted border border-surface-muted/30 text-sm">
                          Regular
                        </span>
                      )}
                    </td>
                    <td className="p-4 text-surface-muted text-sm">
                      <div className="space-y-1">
                        <div className="flex items-center gap-1">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            user.is_unlimited ? 'bg-purple-100 text-purple-700' : 
                            user.subscription_tier === 'premium' ? 'bg-orange-100 text-orange-700' : 
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {user.is_unlimited ? '∞ Unlimited' : 
                             user.subscription_tier === 'premium' ? '⭐ Premium' : 
                             '🆓 Free'}
                          </span>
                        </div>
                        <div className="text-xs text-surface-muted">
                          {user.subscription_status === 'active' ? '✅ Active' : '❌ Inactive'}
                        </div>
                      </div>
                    </td>
                    <td className="p-4 text-surface-muted text-sm">
                      <div className="space-y-1">
                        <div className="flex items-center gap-1">
                          {editingCredits?.userId === user.id ? (
                            <div className="flex items-center gap-1">
                              <input
                                type="number"
                                value={editingCredits.value}
                                onChange={(e) => setEditingCredits({ userId: user.id, value: e.target.value })}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleCreditSave(user.id);
                                  if (e.key === 'Escape') handleCreditCancel();
                                }}
                                className="w-16 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:border-vibrant-orange"
                                min="0"
                                autoFocus
                              />
                              <button
                                onClick={() => handleCreditSave(user.id)}
                                className="text-green-600 hover:text-green-800 text-xs"
                                title="Save"
                              >
                                ✓
                              </button>
                              <button
                                onClick={handleCreditCancel}
                                className="text-red-600 hover:text-red-800 text-xs"
                                title="Cancel"
                              >
                                ✗
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1">
                              <span 
                                className={`font-bold text-lg cursor-pointer hover:bg-gray-100 px-1 rounded ${(user.is_unlimited || user.credits_remaining >= 999999) ? 'text-purple-600' : user.credits_remaining > 50 ? 'text-green-600' : user.credits_remaining > 10 ? 'text-orange-600' : 'text-red-600'}`}
                                onClick={() => !(user.is_unlimited || user.credits_remaining >= 999999) && handleCreditEdit(user.id, user.credits_remaining)}
                                title={!(user.is_unlimited || user.credits_remaining >= 999999) ? "Click to edit credits" : "User has unlimited credits"}
                              >
                                {(user.is_unlimited || user.credits_remaining >= 999999) ? '∞' : Math.floor(user.credits_remaining)}
                              </span>
                              {!(user.is_unlimited || user.credits_remaining >= 999999) && <span className="text-surface-muted">/ {user.monthly_credits_limit}</span>}
                            </div>
                          )}
                        </div>
                        {!(user.is_unlimited || user.credits_remaining >= 999999) && editingCredits?.userId !== user.id && (
                          <div className="w-16 bg-gray-200 rounded-full h-1.5">
                            <div 
                              className={`h-1.5 rounded-full ${user.credits_remaining > 50 ? 'bg-green-500' : user.credits_remaining > 10 ? 'bg-orange-500' : 'bg-red-500'}`}
                              style={{ width: `${Math.min(100, (user.credits_remaining / user.monthly_credits_limit) * 100)}%` }}
                            ></div>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="p-4 text-surface-muted text-sm">
                      <div className="space-y-1">
                        <div className="flex items-center gap-1">
                          <span>📚</span>
                          <span className="text-muted-purple font-medium">{user.total_courses}</span>
                          <span>courses</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <span>🎓</span>
                          <span className="text-vibrant-orange font-medium">{user.total_sessions}</span>
                          <span>sessions</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <span>🏆</span>
                          <span className="text-galaxy-pink font-medium">{user.total_practice_sessions}</span>
                          <span>practice</span>
                        </div>
                      </div>
                    </td>
                    <td className="p-4 text-surface-muted text-sm">
                      {formatDate(user.created_at)}
                    </td>
                    <td className="p-4">
                      <div className="flex flex-col gap-2 min-w-[140px]">
                        {/* VIP Toggle */}
                        <Button
                          size="sm"
                          variant={user.is_vip ? "secondary" : "gradient"}
                          onClick={() => handleToggleVip(user.id, user.is_vip)}
                          loading={updating === user.id}
                          disabled={updating === user.id || creditOperation?.userId === user.id}
                          icon={user.is_vip ? undefined : <Star className="w-3 h-3" />}
                          className={`text-xs ${user.is_vip 
                            ? "hover:bg-surface-muted/30 border border-surface-muted/30" 
                            : "bg-gradient-to-r from-vibrant-orange to-vibrant-orange/90"
                          }`}
                        >
                          {user.is_vip ? 'Remove VIP' : 'Make VIP'}
                        </Button>

                        {/* Credit Management */}
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleResetCredits(user.id)}
                            loading={creditOperation?.userId === user.id && creditOperation.type === 'reset'}
                            disabled={updating === user.id || creditOperation?.userId === user.id || editingCredits?.userId === user.id}
                            icon={<RefreshCw className="w-3 h-3" />}
                            className="text-xs bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200"
                            title="Reset credits to tier default"
                          >
                            Reset
                          </Button>
                          
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleToggleUnlimited(user.id, user.is_unlimited)}
                            loading={creditOperation?.userId === user.id && creditOperation.type === 'unlimited'}
                            disabled={updating === user.id || creditOperation?.userId === user.id || editingCredits?.userId === user.id}
                            icon={<Infinity className="w-3 h-3" />}
                            className={`text-xs ${user.is_unlimited 
                              ? 'bg-purple-50 hover:bg-purple-100 text-purple-700 border border-purple-200' 
                              : 'bg-gray-50 hover:bg-gray-100 text-gray-700 border border-gray-200'
                            }`}
                            title={user.is_unlimited ? "Remove unlimited access" : "Grant unlimited access"}
                          >
                            {user.is_unlimited ? '∞' : '∞'}
                          </Button>
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-surface-primary flex items-center gap-2">
            <Settings className="w-5 h-5 text-muted-purple" />
            🔧 Quick Actions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Button 
              variant="secondary" 
              onClick={loadAdminData}
              icon={<Database className="w-4 h-4" />}
              className="bg-gradient-to-r from-muted-purple/10 to-muted-purple/5 border border-muted-purple/30 hover:bg-muted-purple/20 text-surface-primary hover:text-muted-purple transition-all duration-300"
            >
              Refresh Data 🔄
            </Button>
            <Button 
              variant="ghost" 
              onClick={() => window.open('/api/docs', '_blank')}
              icon={<BarChart3 className="w-4 h-4" />}
              className="bg-gradient-to-r from-galaxy-pink/10 to-galaxy-pink/5 border border-galaxy-pink/30 hover:bg-galaxy-pink/20 text-surface-primary hover:text-galaxy-pink transition-all duration-300"
            >
              API Docs 📚
            </Button>
            <Button 
              variant="ghost" 
              onClick={() => console.log('System stats:', stats)}
              icon={<TrendingUp className="w-4 h-4" />}
              className="bg-gradient-to-r from-vibrant-orange/10 to-vibrant-orange/5 border border-vibrant-orange/30 hover:bg-vibrant-orange/20 text-surface-primary hover:text-vibrant-orange transition-all duration-300"
            >
              Export Stats
            </Button>
          </div>
        </CardContent>
      </Card>
        </>
      )}

      {/* Usage Analytics Tab */}
      {activeTab === 'usage' && <UsageDashboard />}

      {/* Credit Analytics Tab */}
      {activeTab === 'credits' && <CreditUsageDashboard />}
    </div>
  );
};

export default AdminDashboard; 